import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import { Header } from "@/components/Header";
import { PublicFooter } from "@/components/PublicFooter";
import { SEO, SITE_URL } from "@/components/SEO";
import { api } from "@/lib/api";

export default function JournalPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/seo/blog/public/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <p className="font-display text-2xl">Article not found</p>
          <button onClick={() => navigate("/journal")} className="mt-4 text-[var(--brand)] font-semibold">Back to the Journal</button>
        </div>
      </div>
    );
  }
  if (!post) return <div className="min-h-screen"><Header /><p className="mx-auto max-w-3xl px-6 py-24 text-[var(--ink-soft)]">Loading…</p></div>;

  const canonical = `${SITE_URL}/journal/${post.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.meta_description || post.excerpt,
      author: { "@type": "Person", name: post.author || "Roger" },
      publisher: { "@type": "Organization", name: "Customize Studio", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo512.png` } },
      datePublished: post.published_at,
      mainEntityOfPage: canonical,
      url: canonical,
      keywords: post.keyword,
    },
    ...(post.faq && post.faq.length ? [{
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: post.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    }] : []),
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/journal` },
        { "@type": "ListItem", position: 3, name: post.title, item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen" data-testid={`journal-post-${post.slug}`}>
      <SEO title={`${post.title} | Customize Studio`} description={post.meta_description || post.excerpt} canonical={canonical} jsonLd={jsonLd} />
      <Header />
      <article className="mx-auto max-w-3xl px-6 py-16 md:px-10 lg:py-20">
        <nav className="flex items-center gap-1.5 text-xs text-[var(--ink-soft)]">
          <Link to="/" className="hover:text-[var(--brand)]">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/journal" className="hover:text-[var(--brand)]">Journal</Link>
        </nav>
        <div className="mt-6 flex flex-wrap gap-2">
          {(post.tags || []).map((t) => (
            <span key={t} className="rounded-full bg-[var(--surface-2)] px-2.5 py-0.5 text-[11px] font-medium text-[var(--ink-soft)]">{t}</span>
          ))}
        </div>
        <h1 className="mt-4 font-display text-[2rem] font-semibold leading-[1.15] tracking-tight sm:text-4xl">{post.title}</h1>
        <p className="mt-4 text-sm text-[var(--ink-soft)]">By {post.author || "Roger"}{post.published_at ? ` · ${new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}` : ""}</p>

        {post.related_solution && (
          <Link to={`/solutions/${post.related_solution.slug}`} data-testid="article-related-link"
            className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-[var(--line)] bg-[var(--surface-2)] px-5 py-4 transition-colors hover:border-[var(--brand)]">
            <span className="text-sm">
              <span className="font-semibold">See it in action:</span>{" "}
              <span className="text-[var(--brand)] font-semibold underline decoration-[var(--brand)]/30 underline-offset-2">Customize Studio {post.related_solution.label}</span>
            </span>
            <ArrowRight className="h-4 w-4 flex-none text-[var(--brand)]" strokeWidth={2} />
          </Link>
        )}

        <div className="mt-10 space-y-10">
          {(post.sections || []).map((s, i) => (
            <section key={i}>
              {s.heading && <h2 className="font-display text-2xl tracking-tight">{s.heading}</h2>}
              <div className="mt-3 space-y-4 text-[1.0625rem] leading-relaxed text-[var(--ink-soft)]">
                {String(s.body || "").split(/\n\n+/).map((para, j) => <p key={j}>{para}</p>)}
              </div>
            </section>
          ))}
        </div>

        {post.faq && post.faq.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display text-2xl tracking-tight">Frequently asked questions</h2>
            <div className="mt-6 divide-y divide-[var(--line)] border-t border-[var(--line)]">
              {post.faq.map((f, i) => (
                <details key={i} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between font-semibold">
                    {f.q}<ChevronRight className="h-4 w-4 flex-none text-[var(--ink-soft)] transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--ink-soft)]">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <div className="mt-14 rounded-3xl bg-[var(--ink)] px-8 py-12 text-center text-white">
          <p className="font-display text-2xl tracking-tight">{post.cta || "See the finished project before you build it."}</p>
          <Link to="/signup" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)]">
            Get Started <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
          {post.related_solution && (
            <p className="mt-4 text-sm text-white/70">
              Or explore{" "}
              <Link to={`/solutions/${post.related_solution.slug}`} className="font-semibold text-white underline underline-offset-2">
                {post.related_solution.label}
              </Link>
            </p>
          )}
        </div>

        <Link to="/journal" className="mt-10 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand)]">
          <ArrowLeft className="h-4 w-4" /> Back to the Journal
        </Link>
      </article>
      <PublicFooter />
    </div>
  );
}
